import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import type { Cart, CartItem } from "@server/gql/graphql";
import { updateCart } from "@server/requests/checkout/updateCart";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type UpdateCartItem = <T extends boolean = false>(
  input: CartItem,
  async?: T,
) => T extends true ? Promise<Cart | undefined> : void;

export default function useUpdateCartItem() {
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async (cartItem: CartItem) => {
      const cartId = getCartCookie()?.value;
      const { partNo, quantity } = cartItem;
      if (!cartId) return Promise.resolve(undefined);
      return updateCart({
        cartItem: { partNo, quantity },
        locale,
        cartId,
      });
    },
    onMutate: async (incomingItem) => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);

      queryClient.setQueryData<Cart>(["cart"], (currentCart) => {
        if (!currentCart) {
          return;
        }

        const updatedItems = currentCart.items.map((item) => {
          return item.partNo === incomingItem.partNo
            ? {
                ...item,
                quantity: incomingItem.quantity,
                totalGrossAmount: item.price * incomingItem.quantity,
              }
            : item;
        });

        const newTotal = updatedItems.reduce(
          (sum, item) => sum + item.totalGrossAmount,
          0,
        );

        return {
          ...currentCart,
          items: updatedItems,
          totalGrossAmount: newTotal + currentCart.totalFreightInclVat,
        };
      });

      return { previousCart };
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const updateCartItem = useCallback(
    (input: CartItem, async = false) => {
      if (async) return mutateAsync(input);
      mutate(input);
    },
    [mutate, mutateAsync],
  );

  return {
    updateCartItem: updateCartItem as UpdateCartItem,
    isErrorUpdateCartItem: isError,
    isPendingUpdateCartItem: isPending,
    isSuccessUpdateCartItem: isSuccess,
  };
}
